import { useState } from "react";
import { Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushDataLayer } from "@/lib/analytics";

export type LookbookKey = "LB1" | "LB2" | "LB3" | "LB4";

type Props = {
  imageUrl: string;
  lookbook: LookbookKey;
  badgeText: string;
  filename: string;
  /** Optional outfit id to build a deep link in the share payload. */
  outfitId?: string | null;
};

/**
 * Builds a JPG Blob of the lookbook image with the badge text burned into
 * the bottom-right corner. Mirrors the on-screen <span> badge styling:
 *   bg rgba(31,41,55,0.75), white text, rounded-xl, bottom-2 right-2.
 *
 * Sibling to the existing `buildWatermarkedLookbookBlob` (which renders the
 * "like2fit.com" watermark in PNG) — that helper is intentionally untouched.
 */
export async function buildBadgedLookbookBlob(
  imageUrl: string,
  badgeText: string,
): Promise<Blob> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = imageUrl;
  });

  // Branded 2:3 portrait export canvas
  // Vertical layout:
  //   0..150     top band (cream, empty — Canva hook safe-zone)
  //   150..250   cream buffer
  //   250..1250  image area (1000×1000)
  //   1250..1350 cream buffer
  //   1350..1500 bottom band (cream, wordmark centred)
  const CANVAS_W = 1000;
  const CANVAS_H = 1500;
  const IMG_TOP = 250;
  const IMG_BOTTOM = 1250;
  const IMG_SIZE = 1000;
  const CREAM = "#FAECE7";
  const INK = "#0A0A0A";
  const CORAL = "#FF5C5C";

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");

  // Cream background fills the entire canvas (bands, buffers, behind image)
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Fit source within 1000×1000 image area preserving aspect ratio (no crop)
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const scale = Math.min(IMG_SIZE / srcW, IMG_SIZE / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const drawX = (CANVAS_W - drawW) / 2;
  const drawY = IMG_TOP + (IMG_SIZE - drawH) / 2;
  ctx.drawImage(img, drawX, drawY, drawW, drawH);

  // Hairline dividers above and below the image area
  ctx.fillStyle = "rgba(0,0,0,0.08)";
  ctx.fillRect(0, IMG_TOP, CANVAS_W, 1);
  ctx.fillRect(0, IMG_BOTTOM - 1, CANVAS_W, 1);

  // Chip anchored to bottom-right of the IMAGE area (not the full canvas)
  const fontSize = Math.max(14, Math.round(drawW * 0.022));
  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const padX = Math.round(fontSize * 0.83);
  const padY = Math.round(fontSize * 0.33);
  const metrics = ctx.measureText(badgeText);
  const textW = metrics.width;
  const textH = fontSize;
  const pillW = textW + padX * 2;
  const pillH = textH + padY * 2;
  const margin = Math.round(drawW * 0.02);
  const x = drawX + drawW - pillW - margin;
  const y = drawY + drawH - pillH - margin;
  const r = Math.round(fontSize * 0.55);

  ctx.fillStyle = "rgba(31,41,55,0.75)";
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + pillW - r, y);
  ctx.arcTo(x + pillW, y, x + pillW, y + r, r);
  ctx.lineTo(x + pillW, y + pillH - r);
  ctx.arcTo(x + pillW, y + pillH, x + pillW - r, y + pillH, r);
  ctx.lineTo(x + r, y + pillH);
  ctx.arcTo(x, y + pillH, x, y + pillH - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "middle";
  ctx.fillText(badgeText, x + padX, y + pillH / 2);

  // Wordmark in bottom band — Instrument Serif italic, two-tone
  const wmSize = 60;
  try {
    if (typeof document !== "undefined" && "fonts" in document) {
      await document.fonts.load(`italic 400 ${wmSize}px "Instrument Serif"`);
      await document.fonts.ready;
    }
  } catch {
    /* fall back to serif if font load fails */
  }
  ctx.font = `italic 400 ${wmSize}px "Instrument Serif", "Times New Roman", serif`;
  ctx.textBaseline = "middle";
  // Letter-spacing -0.5px: measure parts separately and shift
  const letterSpacing = -0.5;
  const part1 = "like2";
  const part2 = "fit";
  const w1 = ctx.measureText(part1).width + letterSpacing * (part1.length - 1);
  const w2 = ctx.measureText(part2).width + letterSpacing * (part2.length - 1);
  const totalW = w1 + w2 + letterSpacing; // gap between parts
  const startX = (CANVAS_W - totalW) / 2;
  const centerY = 1350 + 150 / 2;

  // Render with manual letter spacing
  const drawSpaced = (text: string, startPx: number, color: string) => {
    ctx.fillStyle = color;
    let cursor = startPx;
    for (const ch of text) {
      ctx.fillText(ch, cursor, centerY);
      cursor += ctx.measureText(ch).width + letterSpacing;
    }
    return cursor;
  };
  const afterPart1 = drawSpaced(part1, startX, INK);
  drawSpaced(part2, afterPart1, CORAL);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("blob_failed"))),
      "image/jpeg",
      0.92,
    );
  });
}


function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function fireTracking(lookbook: LookbookKey, method: "share" | "download") {
  // GA4 — existing in-app gtag (loaded in __root.tsx)
  try {
    const g = (window as unknown as { gtag?: (...args: unknown[]) => void })
      .gtag;
    g?.("event", "lookbook_shared", { lookbook, method });
  } catch {
    /* ignore */
  }
  // Pinterest via GTM dataLayer
  pushDataLayer({ event: "lookbook_shared", lookbook, method });
}

export function LookbookActionButtons({
  imageUrl,
  lookbook,
  badgeText,
  filename,
  outfitId,
}: Props) {
  const [busy, setBusy] = useState<"share" | "download" | null>(null);

  async function onShare() {
    if (busy) return;
    fireTracking(lookbook, "share"); // fire BEFORE any await
    setBusy("share");
    try {
      const blob = await buildBadgedLookbookBlob(imageUrl, badgeText);
      const file = new File([blob], filename, { type: "image/jpeg" });
      const nav = navigator as Navigator & {
        canShare?: (data: { files: File[] }) => boolean;
      };
      const deepLink = outfitId
        ? `https://like2fit.com?outfit=${encodeURIComponent(outfitId)}`
        : "https://like2fit.com";
      const sharePayload: ShareData = {
        files: [file],
        title: "Check out my Like2Fit look",
        text: "See this outfit styled on my body. Try yours at like2fit.com",
        url: deepLink,
      };
      if (
        typeof nav.canShare === "function" &&
        nav.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        await navigator.share(sharePayload);
      } else {
        triggerDownload(blob, filename);
      }
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === "AbortError") return; // user cancelled the share sheet
      toast.error("Couldn't share — try downloading instead.");
    } finally {
      setBusy(null);
    }
  }

  async function onDownload() {
    if (busy) return;
    fireTracking(lookbook, "download"); // fire BEFORE any await
    setBusy("download");
    try {
      const blob = await buildBadgedLookbookBlob(imageUrl, badgeText);
      triggerDownload(blob, filename);
    } catch {
      toast.error("Download failed.");
    } finally {
      setBusy(null);
    }
  }

  const btnBase: React.CSSProperties = {
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    color: "#1f2937",
    border: "none",
    cursor: busy ? "default" : "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    backdropFilter: "blur(6px)",
  };

  return (
    <div
      className="absolute top-2 right-2"
      style={{
        display: "inline-flex",
        gap: 6,
        zIndex: 2,
      }}
    >
      <button
        type="button"
        onClick={onShare}
        disabled={!!busy}
        aria-label="Share lookbook"
        style={{ ...btnBase, background: "rgba(255,255,255,0.95)" }}
      >
        {busy === "share" ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Share2 size={16} />
        )}
      </button>
      <button
        type="button"
        onClick={onDownload}
        disabled={!!busy}
        aria-label="Download lookbook"
        style={{ ...btnBase, background: "rgba(255,255,255,0.75)" }}
      >
        {busy === "download" ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Download size={16} />
        )}
      </button>
    </div>
  );
}
